import React, {ReactElement, useEffect, useState} from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {useAccessToken} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {findBillpayOrgSettings, BillpayOrgSettings} from "../../resources/billpayOrgSettings"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useNavigate} from "react-router-dom"
import {
	DataTable,
	DataTableHead,
	DataTableBody,
	DataTableCell,
	DataTableRow,
	DataTableCard,
	TablePending,
} from "../../components/DataTable/DataTable"
import PagingNavBar from "../../components/PagingNavBar/PagingNavBar"
import moment from "moment"
import Icon from "../../components/Icon/Icon"
import CreateBillPayOrgSettingsModal from "./CreateBillPayOrgSettingsModal"
import {findOrgs} from "../../resources/org"
import {buildOrgNameMap, getOrgId} from "./utils"

const COLUMNS = ["Id", "Org", "Banking Terms ID", "Customer Level Categories", "Auto Link Accounts", "Created At"]

function OrgSettingsRow({settings, orgNameMap}: {settings: BillpayOrgSettings; orgNameMap: Map<string, string>}) {
	const navigate = useNavigate()
	const orgId = getOrgId(settings)
	const orgName = orgId ? orgNameMap.get(orgId) ?? orgId : "--"

	return (
		<DataTableRow onClick={() => navigate(`/billpay-org-settings/${settings.id}`)}>
			<DataTableCell className="data-table-id-cell">{settings.id}</DataTableCell>
			<DataTableCell>{orgName}</DataTableCell>
			<DataTableCell>{settings.attributes.bankingTermsId}</DataTableCell>
			<DataTableCell>{settings.attributes.enableCustomerLevelCategories ? "Yes" : "No"}</DataTableCell>
			<DataTableCell>{settings.attributes.autoLinkAccounts ? "Yes" : "No"}</DataTableCell>
			<DataTableCell>{moment(settings.attributes.createdAt).format("L")}</DataTableCell>
		</DataTableRow>
	)
}

export default function BillPayOrgSettingsList(): ReactElement {
	const accessToken = useAccessToken()
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [orgNameMap, setOrgNameMap] = useState<Map<string, string>>(new Map())
	const [refreshToken, setRefreshToken] = useState(0)

	useEffect(() => {
		findOrgs(accessToken, 0, 10000).then((result) => {
			result.match(
				(orgs) => setOrgNameMap(buildOrgNameMap(orgs)),
				() => undefined
			)
		})
	}, [accessToken])

	const [result, hasPrev, hasNext, prev, next, hasResults] = usePaging(
		40,
		(offset, limit) => findBillpayOrgSettings(accessToken, offset, limit),
		(x) => x.data.length,
		[refreshToken],
		/* prefix */ "",
		/* isPendingOnNavigation */ false,
		/* avoidQueryState */ true
	)

	return (
		<>
			{showCreateModal && (
				<CreateBillPayOrgSettingsModal
					close={() => setShowCreateModal(false)}
					onSuccess={() => setRefreshToken((t) => t + 1)}
				/>
			)}

			<DataTableCard className="billpay-org-settings-card">
				<TitleBar title="Bill Pay Org Settings">
					<button className="button button-create" onClick={() => setShowCreateModal(true)}>
						<Icon icon="interface-add-1" size={12} /> Create
					</button>
				</TitleBar>

				<AsyncResultComponent asyncResult={result} pendingComponent={<TablePending numberOfRows={COLUMNS.length} />}>
					{({value: orgSettings}) => (
						<>
							<DataTable
								isEmpty={orgSettings.data.length === 0}
								fullHeight
								stickyAction
								noContentText="No bill pay org settings found"
							>
								<DataTableHead>
									<DataTableRow>
										{COLUMNS.map((col) => (
											<DataTableCell key={col}>{col}</DataTableCell>
										))}
									</DataTableRow>
								</DataTableHead>
								<DataTableBody>
									{orgSettings.data.map((s) => (
										<OrgSettingsRow key={s.id} settings={s} orgNameMap={orgNameMap} />
									))}
								</DataTableBody>
							</DataTable>
							<PagingNavBar
								hasResults={hasResults}
								hasPrev={hasPrev}
								hasNext={hasNext}
								prev={prev}
								next={next}
								isShow
								meta={orgSettings.meta}
							/>
						</>
					)}
				</AsyncResultComponent>
			</DataTableCard>
		</>
	)
}
